import { useRef, useState } from "react";
import axios from "axios";
import { Upload, X, Image as ImageIcon, Loader2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ATTACHMENT_LABELS = {
  corrective_maintenance: "CMR (Corrective Maintenance Report)",
  before: "Before photo",
  after: "After photo",
};

// CMR accepts PDF in addition to images; before/after are photos only
const isCMR = (type) => type === "corrective_maintenance";
const acceptFor = (type) => (isCMR(type) ? "image/*,application/pdf" : "image/*");
const helperText = (type) =>
  isCMR(type) ? "PDF or image • Max 10MB" : "JPG, PNG, WEBP • Max 10MB";

const AttachmentSlot = ({ workOrderId, type, attachment, onChanged }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    const cmrAllowed = isCMR(type);
    const ok = cmrAllowed
      ? file.type.startsWith("image/") || file.type === "application/pdf"
      : file.type.startsWith("image/");
    if (!ok) {
      toast.error(cmrAllowed ? "Please select an image or PDF file" : "Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/work-orders/${workOrderId}/attachments/${type}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${ATTACHMENT_LABELS[type]} uploaded`);
      onChanged();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleFileSelect = async (e) => {
    await uploadFile(e.target.files?.[0]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await uploadFile(file);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${ATTACHMENT_LABELS[type]}?`)) return;
    try {
      await axios.delete(`${API}/work-orders/${workOrderId}/attachments/${type}`);
      toast.success("Removed");
      onChanged();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  const fileUrl = attachment ? `${API}/files/${attachment.storage_path}` : null;
  const isPdfAttachment = attachment?.content_type === "application/pdf";

  return (
    <div
      className="border border-[#E4E4E7] rounded-sm p-3 bg-white"
      data-testid={`attachment-slot-${type}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#71717A]">
          {ATTACHMENT_LABELS[type]}
        </div>
        {attachment && (
          <button
            type="button"
            onClick={handleDelete}
            className="text-[#71717A] hover:text-[#DC2626] transition-colors"
            aria-label="Remove"
            data-testid={`delete-attachment-${type}`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {fileUrl ? (
        isPdfAttachment ? (
          <a
            href={fileUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full h-40 rounded-sm border border-[#E4E4E7] bg-[#F4F4F5] flex flex-col items-center justify-center gap-2 hover:bg-[#E4E4E7] transition-colors"
            data-testid={`attachment-pdf-${type}`}
          >
            <div className="w-12 h-12 rounded-sm bg-[#DC2626] flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs font-semibold text-[#09090B]">PDF Document</span>
            <span className="text-[10px] text-[#71717A] truncate max-w-[95%] px-2">
              {attachment.original_filename || "cmr.pdf"}
            </span>
          </a>
        ) : (
          <a href={fileUrl} target="_blank" rel="noreferrer" className="block">
            <img
              src={fileUrl}
              alt={ATTACHMENT_LABELS[type]}
              className="w-full h-40 object-cover rounded-sm border border-[#E4E4E7]"
              data-testid={`attachment-image-${type}`}
            />
          </a>
        )
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={uploading}
          className={`w-full h-40 border-2 border-dashed rounded-sm flex flex-col items-center justify-center gap-2 transition-all group disabled:opacity-50 ${
            isDragging
              ? "border-[#0F172A] bg-[#F4F4F5] scale-[1.02]"
              : "border-[#E4E4E7] hover:bg-[#F4F4F5] hover:border-[#0F172A]"
          }`}
          data-testid={`upload-btn-${type}`}
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 text-[#71717A] animate-spin" />
          ) : (
            <>
              <div className={`w-10 h-10 rounded-sm flex items-center justify-center transition-colors ${
                isDragging ? "bg-[#0F172A]" : "bg-[#F4F4F5] group-hover:bg-white"
              }`}>
                <Upload className={`w-5 h-5 ${isDragging ? "text-white" : "text-[#71717A]"}`} />
              </div>
              <span className="text-xs text-[#71717A] font-medium">
                {isDragging ? "Drop file here" : "Click or drag & drop"}
              </span>
              <span className="text-[10px] text-[#71717A]">{helperText(type)}</span>
            </>
          )}
        </button>
      )}

      {attachment && !uploading && (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full mt-2 text-xs text-[#71717A] hover:text-[#0F172A] transition-colors"
          data-testid={`replace-btn-${type}`}
        >
          Replace {isPdfAttachment ? "document" : "photo"}
        </button>
      )}

      <input
        type="file"
        ref={fileRef}
        onChange={handleFileSelect}
        accept={acceptFor(type)}
        className="hidden"
        data-testid={`file-input-${type}`}
      />
    </div>
  );
};

const AttachmentUploader = ({ workOrderId, attachments, onChanged }) => {
  const atts = attachments || {};
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">
        <ImageIcon className="w-4 h-4" />
        Closure Evidence — CMR & Photos
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <AttachmentSlot
          workOrderId={workOrderId}
          type="corrective_maintenance"
          attachment={atts.corrective_maintenance}
          onChanged={onChanged}
        />
        <AttachmentSlot
          workOrderId={workOrderId}
          type="before"
          attachment={atts.before}
          onChanged={onChanged}
        />
        <AttachmentSlot
          workOrderId={workOrderId}
          type="after"
          attachment={atts.after}
          onChanged={onChanged}
        />
      </div>
    </div>
  );
};

export default AttachmentUploader;
